import { Component, OnDestroy, OnInit } from '@angular/core';
import Map from 'ol/Map';
import { fromLonLat } from 'ol/proj';
import { Feature } from 'ol';
import { LineString, Point } from 'ol/geom';
import { Icon, Stroke, Style } from 'ol/style';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { HttpClient } from '@angular/common/http';
import { SelectedLeadsRoutingComponent } from './selected-leads-routing/selected-leads-routing.component';
import { TileService } from './tile.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LeadService } from '../../../services/leads/lead.service';
import { GeolocationService } from '../../../services/geolocation/geolocation.service';
import { RouteInformationService } from '../../route-information.service';
import { Lead } from '../../../lead.model';
import { initMap } from '../../../reusable-components/open-layers.utils';

@Component({
  selector: 'app-selected-leads-map',
  templateUrl: './selected-leads-map.component.html',
  styleUrl: './selected-leads-map.component.scss',
  imports: [SelectedLeadsRoutingComponent],
  providers: [],
})
export class SelectedLeadsMapComponent implements OnInit, OnDestroy {
  mapInformationSubscription: Subscription | undefined;
  currentLeadSubscription: Subscription | undefined;
  routingFeature: Feature<LineString>;
  leadVectorSource = new VectorSource();
  public map!: Map;
  languageSubscription: Subscription | undefined;

  constructor(
    private leadsService: LeadService,
    private geolocationService: GeolocationService,
    protected http: HttpClient,
    private tileService: TileService,
    private translateService: TranslateService,
    private routeInformationService: RouteInformationService,
  ) {
    this.routingFeature = this.createRoutingFeatures();
  }

  private createRoutingFeatures(): Feature<LineString> {
    const routingFeature = new Feature<LineString>();
    routingFeature.setStyle(
      new Style({
        stroke: new Stroke({
          color: 'red',
          width: 3,
        }),
      }),
    );
    return routingFeature;
  }

  ngOnInit() {
    this.setupMyPosition();
    this.initMapAndAddLeads();
    this.setupClickListenerOnMap();
    this.setupReactionToLeadSelection();
  }

  private setupMyPosition() {
    this.geolocationService.getPosition().then((position) => {
      const currentView = this.map.getView();
      currentView.animate({
        center: fromLonLat([
          position.coords.longitude,
          position.coords.latitude,
        ]),
        duration: 500,
      });
      const meFeature = new Feature({
        geometry: new Point(
          fromLonLat([position.coords.longitude, position.coords.latitude]),
        ),
      });
      meFeature.setId('Du');

      const meImg = document.createElement('img');
      meImg.src = '../assets/material_icon_my_location.png';
      meImg.onload = () => {
        const meStyle = new Style({
          image: new Icon({
            anchor: [0.5, 0.5],
            img: meImg,
            scale: 0.4,
          }),
        });
        meFeature.setStyle(meStyle);
        const meSource = new VectorSource();
        meSource.addFeature(meFeature);

        this.map.addLayer(
          new VectorLayer({
            source: meSource,
          }),
        );
        this.geolocationService.currentPosition$.subscribe((position) => {
          meFeature.setGeometry(
            new Point(
              fromLonLat([position.coords.longitude, position.coords.latitude]),
            ),
          );
        });
      };
    });
  }

  private initMapAndAddLeads() {
    const lineSource = new VectorSource();
    lineSource.addFeature(this.routingFeature!);

    const initialized = initMap(
      [lineSource, this.leadVectorSource],
      this.tileService,
      'selectedLeadsMap',
      this.translateService,
    );
    this.map = initialized.map;
    this.languageSubscription = initialized.subscription;
  }

  private setupClickListenerOnMap() {
    this.map.on('singleclick', (evt) => {
      const feature = this.map.forEachFeatureAtPixel(
        evt.pixel,
        (feature) => feature,
        { hitTolerance: 10 },
      );
      if (feature && feature instanceof Feature) {
        const id = String(feature.getId());
        const lead = this.leadsService.getLead(id);
        if (this.leadsService.isSelected(id) && lead !== undefined) {
          this.routeInformationService.setCurrentLead(lead);
        }
      }
    });
  }

  private setupReactionToLeadSelection() {
    this.currentLeadSubscription =
      this.routeInformationService.currentLead$.subscribe(
        (currentLead: Lead | undefined) => {
          if (currentLead !== undefined) {
            const currentView = this.map.getView();
            currentView.animate({
              center: fromLonLat([
                currentLead.coordinates.longitude,
                currentLead.coordinates.latitude,
              ]),
              duration: 500,
            });
          }
        },
      );
    this.mapInformationSubscription =
      this.routeInformationService.redrawRoute$.subscribe(() => {
        const currentLead = this.routeInformationService.getCurrentLead();
        const mapInformation = this.routeInformationService.getMapInformation();
        const addFeature = (
          imageSrc: (selected: boolean) => string,
          lead: Lead,
        ) => {
          const feat = new Feature({
            geometry: new Point(
              fromLonLat([
                lead.coordinates.longitude,
                lead.coordinates.latitude,
              ]),
            ),
          });
          const icon = document.createElement('img');
          icon.src = imageSrc(lead.id === currentLead?.id);
          icon.onload = () => {
            const style = new Style({
              image: new Icon({
                anchor: [0.5, 1],
                img: icon,
                scale: 0.4,
              }),
            });
            feat.setId(lead.id);
            feat.setStyle(style);
            this.leadVectorSource.addFeature(feat);
          };
        };
        if (mapInformation.type === 'leadInformation') {
          this.leadVectorSource.clear(true);
          for (const lead of mapInformation.leads) {
            const imageSrc = (selected: boolean) =>
              selected
                ? '../../assets/material_icon_location_filled.png'
                : '../../assets/material_icon_location.png';
            addFeature(imageSrc, lead);
          }
        } else if (mapInformation.type === 'routeInformation') {
          this.leadVectorSource.clear(true);
          this.routingFeature.setGeometry(
            new LineString(
              mapInformation.routeElements
                .map((routeElement) =>
                  routeElement.route.map((coord) => fromLonLat(coord)),
                )
                .flat(),
            ),
          );

          const leads = mapInformation.routeElements.map(
            (routeElement) => routeElement.targetLead,
          );
          this.leadVectorSource.clear(true);
          for (let i = 0; i < leads.length; i++) {
            const imageSrc = (selected: boolean) =>
              selected
                ? '../../assets/numbers_red/' + String(i + 1) + '.png'
                : '../../assets/numbers/' + String(i + 1) + '.png';
            addFeature(imageSrc, leads[i]);
          }
          for (const lead of mapInformation.unreachableLeads) {
            const imageSrc = (selected: boolean) =>
              selected
                ? '../../assets/numbers_red/x.png'
                : '../../assets/numbers/x.png';
            addFeature(imageSrc, lead);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.mapInformationSubscription?.unsubscribe();
    this.currentLeadSubscription?.unsubscribe();
    this.languageSubscription?.unsubscribe();
  }
}
